import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject, input, model } from '@angular/core';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { DAY_OF_MONTH_ROWS, DAY_OF_MONTH_TAIL, DayOfMonthValue, LAST_DAY_OF_MONTH, needsLastDayFootnote, toggleDayOfMonth } from './day.of.month.grid.model';
import { dayOfMonthGridBridgeStyles, dayOfMonthGridStyleId } from './day.of.month.grid.theme';

/**
 * Multi-select grid of days of the month, for a monthly recurrence: pick the 1st and the 15th, or the
 * last day whatever number that is.
 *
 * The "Last day" cell is a distinct value rather than the number 31, because a monthly schedule that
 * means "the end of the month" has to land on the 28th in February and the 30th in April. That is also
 * why 29, 30, and 31 raise the footnote and `'last'` does not — those three are absent from some months
 * and get silently pulled back to the last day, which the reader deserves to be told before they save.
 */
@Component({
  selector: 'wb-day-of-month-grid',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: 'day.of.month.grid.component.html',
  styleUrls: ['./day.of.month.grid.component.less'],
})
export class DayOfMonthGridComponent {

  private readonly document = inject(DOCUMENT);

  /** Selected days. Two-way: cells toggle straight into it. */
  public readonly $value = model<DayOfMonthValue[]>([], { alias: 'value' });

  public readonly $disabled = input<boolean>(false, { alias: 'disabled' });

  protected readonly rows = DAY_OF_MONTH_ROWS;

  protected readonly tail = DAY_OF_MONTH_TAIL;

  protected readonly lastDay = LAST_DAY_OF_MONTH;

  protected readonly $showFootnote = computed(() => needsLastDayFootnote(this.$value()));

  public constructor() {

    DesignSystem.Mount(this.document, dayOfMonthGridStyleId, dayOfMonthGridBridgeStyles);
  }

  protected onToggle(day: DayOfMonthValue): void {

    if (this.$disabled()) return;

    this.$value.update(current => toggleDayOfMonth(current, day));
  }

  protected isSelected(day: DayOfMonthValue): boolean {

    return this.$value().includes(day);
  }
}
